/**
 * 租户初始化向导组件
 * 引导新租户选择角色模板并完成初始化设置
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Steps, Button, Space, Checkbox, Tag, List, Result,
  Spin, Alert, Typography, Descriptions, message, Modal,
} from 'antd';
import { modal } from '@/utils/antdStatic';
import {
  TeamOutlined, SafetyCertificateOutlined, CheckCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import {
  getAvailableTemplates,
  previewTemplate,
  initTenant,
} from '../../api/roleTemplate';
import type { RoleTemplate, RoleTemplateDetail, TenantInitResult } from '../../api/types';

const { Title, Text, Paragraph } = Typography;

interface TenantInitializeWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (result: TenantInitResult) => void;
}

const TenantInitializeWizard: React.FC<TenantInitializeWizardProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const [previewData, setPreviewData] = useState<RoleTemplateDetail | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initResult, setInitResult] = useState<TenantInitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 加载可用模板
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await getAvailableTemplates();
      if (res?.data) {
        const data = Array.isArray(res.data) ? res.data : (res.data as any).list || [];
        setTemplates(data);
        // 默认选中系统模板
        const systemIds = data
          .filter((t: RoleTemplate) => t.scope === 'system')
          .map((t: RoleTemplate) => t.id);
        setSelectedTemplateIds(systemIds);
      }
    } catch (err) {
      console.error('获取模板列表失败', err);
      setError('无法加载角色模板列表');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setInitResult(null);
      setError(null);
      fetchTemplates();
    }
  }, [open]);

  // 切换模板选中
  const handleTemplateToggle = (id: number) => {
    setSelectedTemplateIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // 预览模板
  const handlePreview = async (id: number) => {
    setPreviewLoading(true);
    try {
      const res = await previewTemplate(id);
      if (res?.data) {
        setPreviewData(res.data as any);
      }
    } catch (err) {
      console.error('预览失败', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 执行初始化
  const handleInitialize = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await initTenant({
        templateIds: selectedTemplateIds.length > 0 ? selectedTemplateIds : undefined,
      });
      if (res?.data) {
        const result = res.data as any;
        setInitResult(result);
        setCurrentStep(2);
        message.success('租户初始化完成');
        onSuccess?.(result);
      }
    } catch (err: any) {
      setError(err?.message || '初始化失败');
      console.error('初始化失败', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (selectedTemplateIds.length === 0) {
        message.warning('请至少选择一个角色模板');
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      modal.confirm({
        title: '确认初始化',
        content: `即将使用 ${selectedTemplateIds.length} 个模板初始化租户角色。此操作将创建对应的角色和权限配置。`,
        onOk: handleInitialize,
      });
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const getScopeColor = (scope: string) => {
    const map: Record<string, string> = {
      system: 'red',
      global: 'blue',
      tenant: 'green',
    };
    return map[scope] || 'default';
  };

  const getScopeLabel = (scope: string) => {
    const map: Record<string, string> = {
      system: '系统模板',
      global: '全局模板',
      tenant: '租户模板',
    };
    return map[scope] || scope;
  };

  // Step 1: 选择模板
  const renderTemplateSelection = () => (
    <div style={{ marginTop: 24 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="加载可用模板..."><div style={{ minHeight: 100 }} /></Spin>
        </div>
      ) : (
        <List
          dataSource={templates}
          renderItem={(template) => (
            <List.Item
              key={template.id}
              style={{
                padding: '12px 16px',
                border: selectedTemplateIds.includes(template.id)
                  ? '2px solid #1890ff'
                  : '1px solid #f0f0f0',
                borderRadius: 8,
                marginBottom: 8,
                cursor: 'pointer',
                background: selectedTemplateIds.includes(template.id) ? '#e6f7ff' : 'white',
              }}
              onClick={() => handleTemplateToggle(template.id)}
              actions={[
                <Button
                  key="preview"
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(template.id);
                  }}
                >
                  预览
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Checkbox
                    checked={selectedTemplateIds.includes(template.id)}
                    onChange={() => handleTemplateToggle(template.id)}
                  />
                }
                title={
                  <Space>
                    <span>{template.name}</span>
                    <Tag color={getScopeColor(template.scope)}>
                      {getScopeLabel(template.scope)}
                    </Tag>
                    {template.isReadonly === 1 && <Tag>只读</Tag>}
                  </Space>
                }
                description={
                  <Space>
                    <span>{template.description || '无描述'}</span>
                    <Tag>
                      权限: {template.permissionCount}
                    </Tag>
                    <Tag>
                      菜单: {template.menuCount}
                    </Tag>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}

      {/* 模板预览 */}
      {previewData && (
        <Modal
          title={`模板预览: ${previewData.name}`}
          open={!!previewData}
          onCancel={() => setPreviewData(null)}
          footer={null}
          width={600}
        >
          {previewLoading ? (
            <Spin />
          ) : (
            <>
              <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="名称">{previewData.name}</Descriptions.Item>
                <Descriptions.Item label="编码">{previewData.code}</Descriptions.Item>
                <Descriptions.Item label="范围">
                  <Tag color={getScopeColor(previewData.scope)}>
                    {getScopeLabel(previewData.scope)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="数据范围">{previewData.dataScope}</Descriptions.Item>
                <Descriptions.Item label="描述" span={2}>
                  {previewData.description || '-'}
                </Descriptions.Item>
              </Descriptions>

              {previewData.permissions?.length > 0 && (
                <Card title={`权限 (${previewData.permissions.length})`} size="small" style={{ marginBottom: 8 }}>
                  <Space wrap>
                    {previewData.permissions.map((p) => (
                      <Tag key={p.id}>{p.name} ({p.code})</Tag>
                    ))}
                  </Space>
                </Card>
              )}

              {previewData.menus?.length > 0 && (
                <Card title={`菜单 (${previewData.menus.length})`} size="small">
                  <Space wrap>
                    {previewData.menus.map((m) => (
                      <Tag key={m.id}>{m.name}</Tag>
                    ))}
                  </Space>
                </Card>
              )}
            </>
          )}
        </Modal>
      )}
    </div>
  );

  // Step 2: 确认信息
  const renderConfirmation = () => {
    const selectedTemplates = templates.filter(t => selectedTemplateIds.includes(t.id));
    return (
      <div style={{ marginTop: 24 }}>
        <Alert
          message="请确认以下初始化配置"
          description="初始化将根据所选模板创建角色和权限配置。如果已存在相同角色，将跳过创建。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Card title="已选择的角色模板" size="small">
          <List
            size="small"
            dataSource={selectedTemplates}
            renderItem={(t) => (
              <List.Item key={t.id}>
                <Space>
                  <SafetyCertificateOutlined />
                  <span>{t.name}</span>
                  <Tag color={getScopeColor(t.scope)}>{getScopeLabel(t.scope)}</Tag>
                  <Text type="secondary">权限:{t.permissionCount} | 菜单:{t.menuCount}</Text>
                </Space>
              </List.Item>
            )}
          />
        </Card>

        <Descriptions
          size="small"
          bordered
          column={1}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="模板数量">{selectedTemplates.length}</Descriptions.Item>
          <Descriptions.Item label="预计创建角色数">{selectedTemplates.length}</Descriptions.Item>
          <Descriptions.Item label="总权限数">
            {selectedTemplates.reduce((sum, t) => sum + (t.permissionCount || 0), 0)}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  };

  // Step 3: 完成
  const renderResult = () => (
    <div style={{ marginTop: 24 }}>
      {initResult ? (
        <Result
          status="success"
          title="租户初始化完成"
          subTitle={`成功创建 ${initResult.roleCount} 个角色`}
          extra={[
            <Button key="close" type="primary" onClick={onClose}>
              完成
            </Button>,
          ]}
        >
          {initResult.roles?.length > 0 && (
            <Card title="已创建的角色" size="small" style={{ textAlign: 'left' }}>
              <List
                size="small"
                dataSource={initResult.roles}
                renderItem={(role) => (
                  <List.Item key={role.id}>
                    <Space>
                      <TeamOutlined />
                      <span>{role.name}</span>
                      <Tag>{role.code}</Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Result>
      ) : (
        <Result status="error" title="初始化失败" subTitle={error} />
      )}
    </div>
  );

  const steps = [
    { title: '选择模板', icon: <SafetyCertificateOutlined /> },
    { title: '确认配置', icon: <CheckCircleOutlined /> },
    { title: '完成', icon: <RocketOutlined /> },
  ];

  return (
    <Modal
      title="租户初始化向导"
      open={open}
      onCancel={onClose}
      width={720}
      footer={
        currentStep < 2 ? (
          <Space>
            {currentStep > 0 && (
              <Button onClick={handlePrev}>上一步</Button>
            )}
            <Button
              type="primary"
              onClick={handleNext}
              loading={submitting}
            >
              {currentStep === 1 ? '开始初始化' : '下一步'}
            </Button>
          </Space>
        ) : null
      }
      destroyOnHidden
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 8 }} />

      {error && currentStep < 2 && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginTop: 16 }}
        />
      )}

      {currentStep === 0 && renderTemplateSelection()}
      {currentStep === 1 && renderConfirmation()}
      {currentStep === 2 && renderResult()}
    </Modal>
  );
};

export default TenantInitializeWizard;
